import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import type {
  RFICreate,
  RFIUpdate,
  RFIStatusUpdate,
  RFIAnswerSubmit,
  SendRFIEmailRequest,
  RFIListOptions,
} from '@/types/rfis';

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetches all RFIs for a specific project.
 *
 * @param projectId - Project ID to fetch RFIs for
 * @param options   - Optional status/search filters
 * @returns Query result with rfis array and total count
 *
 * @example
 * const { data, isLoading } = useRFIs(projectId);
 * const rfis = data?.rfis ?? [];
 */
export function useRFIs(projectId: string | undefined, options: RFIListOptions = {}) {
  return useQuery({
    queryKey: ['rfis', projectId, options],
    queryFn: async () => {
      const response = await api.rfis.list(projectId!, options);
      return response;
    },
    enabled: !!projectId,
  });
}

/**
 * Fetches a single RFI with full detail and attached files.
 *
 * @param rfiId - The RFI ID to fetch
 * @returns Query result with rfi object and files array
 *
 * @example
 * const { data } = useRFI(rfiId);
 * const rfi = data?.rfi;
 * const files = data?.files ?? [];
 */
export function useRFI(rfiId: string | null | undefined) {
  return useQuery({
    queryKey: ['rfis', rfiId],
    queryFn: async () => {
      const response = await api.rfis.get(rfiId!);
      return response;
    },
    enabled: !!rfiId,
  });
}

// ============================================================================
// Mutation Hooks — CRUD
// ============================================================================

/**
 * Creates a new RFI.
 *
 * Cache invalidation: Invalidates all RFI queries for the project.
 *
 * @example
 * const createRFI = useCreateRFI();
 * await createRFI.mutateAsync({ projectId: 'proj-123', rfi: { subject: 'Beam depth' } });
 */
export function useCreateRFI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { projectId: string; rfi: RFICreate }) =>
      api.rfis.create(data.projectId, data.rfi),
    onSuccess: (response) => {
      const newRFI = response?.rfi || response;
      // Update cache directly — no accordion collapse/re-expand needed
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'rfis' && query.queryKey.length === 3 },
        (old) => {
          if (!old?.rfis) return old;
          return { ...old, rfis: [...old.rfis, newRFI], total: (old.total ?? 0) + 1 };
        }
      );
      // Don't invalidate ['rfis'] — we just set that cache.
      toast.success('RFI created successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create RFI');
    },
  });
}

/**
 * Updates an existing RFI (fields only — use useUpdateRFIStatus for status).
 *
 * @example
 * const updateRFI = useUpdateRFI();
 * await updateRFI.mutateAsync({ id: 'rfi-123', data: { subject: 'Updated subject' } });
 */
export function useUpdateRFI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RFIUpdate }) =>
      api.rfis.update(id, data),
    onSuccess: (response, variables) => {
      const updatedRFI = response?.rfi || response;
      // Update the list cache (queryKey has 3 parts: ['rfis', projectId, options])
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'rfis' && query.queryKey.length === 3 },
        (old) => {
          if (!old?.rfis) return old;
          return {
            ...old,
            rfis: old.rfis.map((r: any) =>
              r.id === variables.id ? { ...r, ...updatedRFI } : r
            ),
          };
        }
      );
      // Update the single-RFI cache (queryKey has 2 parts: ['rfis', rfiId])
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'rfis' && query.queryKey[1] === variables.id },
        (old) => {
          if (!old?.rfi) return old;
          return { ...old, rfi: { ...old.rfi, ...updatedRFI } };
        }
      );
      // Don't invalidate — we just set both caches from the server response.
      toast.success('RFI updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update RFI');
    },
  });
}

/**
 * Deletes an RFI (also deletes all attached files on the backend).
 *
 * @example
 * const deleteRFI = useDeleteRFI();
 * await deleteRFI.mutateAsync('rfi-123');
 */
export function useDeleteRFI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (rfiId: string) => api.rfis.delete(rfiId),
    onSuccess: (_, rfiId) => {
      // Remove from list cache immediately
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'rfis' && query.queryKey.length === 3 },
        (old) => {
          if (!old?.rfis) return old;
          return {
            ...old,
            rfis: old.rfis.filter((r: any) => r.id !== rfiId),
            total: Math.max(0, (old.total ?? 1) - 1),
          };
        }
      );
      // Don't invalidate ['rfis'] — we just set that cache.
      toast.success('RFI deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete RFI');
    },
  });
}

// ============================================================================
// Mutation Hooks — Workflow actions
// ============================================================================

/**
 * Updates RFI status via the validated transition endpoint.
 *
 * Backend enforces VALID_TRANSITIONS rules.
 * For draft→open, prefer useSendRFIEmail which does both actions atomically.
 *
 * @example
 * const updateStatus = useUpdateRFIStatus();
 * await updateStatus.mutateAsync({ rfiId: 'rfi-123', status: 'closed' });
 */
export function useUpdateRFIStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ rfiId, status }: { rfiId: string; status: RFIStatusUpdate['status'] }) =>
      api.rfis.updateStatus(rfiId, { status }),
    onSuccess: (response, variables) => {
      const updatedRFI = response?.rfi || response;
      // Update list cache
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'rfis' && query.queryKey.length === 3 },
        (old) => {
          if (!old?.rfis) return old;
          return {
            ...old,
            rfis: old.rfis.map((r: any) =>
              r.id === variables.rfiId ? { ...r, ...updatedRFI } : r
            ),
          };
        }
      );
      // Update single-RFI cache
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'rfis' && query.queryKey[1] === variables.rfiId },
        (old) => {
          if (!old?.rfi) return old;
          return { ...old, rfi: { ...old.rfi, ...updatedRFI } };
        }
      );
      // Don't invalidate — we just set both caches from the server response.
      toast.success('RFI status updated');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update RFI status');
    },
  });
}

/**
 * Submits an answer for an open RFI (atomic: sets answer + status='answered').
 *
 * @example
 * const submitAnswer = useSubmitRFIAnswer();
 * await submitAnswer.mutateAsync({ rfiId: 'rfi-123', data: { answer: 'Use 300mm beams' } });
 */
export function useSubmitRFIAnswer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ rfiId, data }: { rfiId: string; data: RFIAnswerSubmit }) =>
      api.rfis.submitAnswer(rfiId, data),
    onSuccess: (response, variables) => {
      const updatedRFI = response?.rfi || response;
      // Update list cache
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'rfis' && query.queryKey.length === 3 },
        (old) => {
          if (!old?.rfis) return old;
          return {
            ...old,
            rfis: old.rfis.map((r: any) =>
              r.id === variables.rfiId ? { ...r, ...updatedRFI } : r
            ),
          };
        }
      );
      // Update single-RFI cache
      queryClient.setQueriesData<any>(
        { predicate: (query) => query.queryKey[0] === 'rfis' && query.queryKey[1] === variables.rfiId },
        (old) => {
          if (!old?.rfi) return old;
          return { ...old, rfi: { ...old.rfi, ...updatedRFI } };
        }
      );
      // Don't invalidate — we just set both caches from the server response.
      toast.success('Answer submitted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to submit answer');
    },
  });
}

/**
 * Sends an RFI via email.
 * If RFI is 'draft', automatically transitions to 'open' and sets date_issued.
 *
 * @example
 * const sendRFI = useSendRFIEmail();
 * await sendRFI.mutateAsync({ rfiId: 'rfi-123', data: { recipient_email: 'arch@firm.com' } });
 */
export function useSendRFIEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ rfiId, data }: { rfiId: string; data: SendRFIEmailRequest }) =>
      api.rfis.send(rfiId, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['rfis'] });
      toast.success(`RFI sent to ${response.sent_to}`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to send RFI');
    },
  });
}

// ============================================================================
// Mutation Hooks — File attachments
// ============================================================================

/**
 * Uploads a file attachment to an RFI.
 * file_type='question' for documents supporting the question.
 * file_type='answer' for documents included in the answer.
 *
 * @example
 * const upload = useUploadRFIFile();
 * await upload.mutateAsync({ rfiId: 'rfi-123', file, fileType: 'question' });
 */
export function useUploadRFIFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      rfiId,
      file,
      fileType = 'question',
    }: {
      rfiId: string;
      file: File;
      fileType?: 'question' | 'answer';
    }) => api.rfis.files.upload(rfiId, file, fileType),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rfis', variables.rfiId] });
      queryClient.invalidateQueries({ queryKey: ['rfis'] });
      toast.success('File uploaded');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to upload file');
    },
  });
}

/**
 * Deletes a file attachment from an RFI.
 *
 * @example
 * const deleteFile = useDeleteRFIFile();
 * await deleteFile.mutateAsync({ rfiId: 'rfi-123', fileId: 'file-456' });
 */
export function useDeleteRFIFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ rfiId, fileId }: { rfiId: string; fileId: string }) =>
      api.rfis.files.delete(rfiId, fileId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rfis', variables.rfiId] });
      queryClient.invalidateQueries({ queryKey: ['rfis'] });
      toast.success('File deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete file');
    },
  });
}
